import { redactValue } from "@repo/shared";
import * as Sentry from "@sentry/react";
import {
  getAppErrorData,
  isExpectedAppError,
  isExpectedUnauthenticatedError,
} from "./convex-errors";

/**
 * Generate a unique request ID for correlation across breadcrumbs and errors.
 */
export function newRequestId(): string {
  return crypto.randomUUID();
}

/**
 * Breadcrumb categories for consistent filtering in Sentry.
 */
export type BreadcrumbCategory =
  | "mutation"
  | "action"
  | "navigation"
  | "user"
  | "app"
  | "network"
  | "spellcheck";

/**
 * Add a breadcrumb to Sentry for debugging context.
 * Breadcrumbs appear in the timeline when an error is captured.
 */
export function breadcrumb(
  event: string,
  data?: Record<string, unknown>,
  options?: {
    category?: BreadcrumbCategory;
    level?: Sentry.SeverityLevel;
  }
) {
  Sentry.addBreadcrumb({
    category: options?.category ?? "app",
    message: event,
    data: data ? sanitize(data) : undefined,
    level: options?.level ?? "info",
  });
}

export function sanitize(input: Record<string, unknown>): Record<string, unknown> {
  const clone: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    clone[key] = key === "requestId" ? value : redactValue(key, value);
  }
  return clone;
}

const OBSERVED_ERROR_MARKER = Symbol.for("df.observability.errorObserved");

/**
 * Mark an error as already handled by observability (captured, or deliberately not captured for
 * expected auth states), so boundary handlers that settle the promise can avoid double capture.
 */
export function markErrorObserved(error: unknown) {
  if (error !== null && typeof error === "object") {
    try {
      (error as Record<symbol, unknown>)[OBSERVED_ERROR_MARKER] = true;
    } catch {
      // A frozen error stays unmarked; the worst case is a duplicate capture.
    }
  }
}

export function isErrorObserved(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === "object" &&
    Boolean((error as Record<symbol, unknown>)[OBSERVED_ERROR_MARKER])
  );
}

/**
 * Capture an exception to Sentry with consistent tagging.
 */
export function captureError(
  error: unknown,
  context: {
    feature: string;
    operation: string;
    requestId?: string;
    tags?: Record<string, string | number | boolean | undefined>;
    fingerprint?: string[];
    extra?: Record<string, unknown>;
  }
) {
  Sentry.captureException(error, {
    tags: {
      feature: context.feature,
      operation: context.operation,
      ...(context.requestId && { requestId: context.requestId }),
      ...context.tags,
    },
    fingerprint: context.fingerprint,
    extra: context.extra ? sanitize(context.extra) : undefined,
  });
}

export type ObservedOperationKind = "mutation" | "action";

export type ObservedFailureClassifier<T> = (result: T) => string | null | undefined | false;

/**
 * Track a user-triggered async operation with request correlation, breadcrumbs,
 * Sentry capture on thrown failures, and optional capture for `{ success: false }`
 * style results that preserve return shapes.
 *
 * Expected failures — missing identity and coded app errors the UI handles as
 * normal outcomes (see `isExpectedAppError`) — get warning breadcrumbs and are
 * rethrown without Sentry capture. So does an `AbortError` `DOMException`: the caller
 * stopped the operation itself, which is not a failure.
 */
export async function trackObservedOperation<T>(options: {
  kind: ObservedOperationKind;
  feature: string;
  operation: string;
  execute: (requestId: string) => Promise<T>;
  context?: Record<string, unknown>;
  failureOutcome?: string;
  classifyFailure?: ObservedFailureClassifier<T>;
}): Promise<T> {
  const {
    kind,
    feature,
    operation,
    execute,
    context = {},
    failureOutcome = "failure",
    classifyFailure,
  } = options;
  const requestId = newRequestId();
  const startTime = Date.now();
  const category = kind;

  breadcrumb(`${feature}.${operation}.start`, { requestId, kind, ...context }, { category });

  try {
    const result = await execute(requestId);
    const durationMs = Date.now() - startTime;
    const failureMessage = classifyFailure?.(result);

    if (failureMessage) {
      breadcrumb(
        `${feature}.${operation}.failure_result`,
        { requestId, kind, durationMs, error: failureMessage, ...context },
        { category, level: "error" }
      );
      captureError(new Error(failureMessage), {
        feature,
        operation,
        requestId,
        extra: { kind, durationMs, outcome: "failure_result", ...context },
      });
      return result;
    }

    breadcrumb(
      `${feature}.${operation}.complete`,
      { requestId, kind, durationMs, ...context },
      { category }
    );

    return result;
  } catch (error) {
    const durationMs = Date.now() - startTime;
    const isExpectedAuthState = isExpectedUnauthenticatedError(error);
    const isExpectedAppFailure = isExpectedAppError(error);
    const isAborted = error instanceof DOMException && error.name === "AbortError";
    const appError = getAppErrorData(error);
    const outcome = isExpectedAuthState
      ? "auth_denied"
      : isExpectedAppFailure
        ? "expected_failure"
        : isAborted
          ? "aborted"
          : failureOutcome;

    breadcrumb(
      `${feature}.${operation}.error`,
      {
        requestId,
        kind,
        durationMs,
        outcome,
        // ConvexError messages are redacted to "Server Error" in production; the
        // coded app-error data carries the user-safe message, so prefer it.
        error: appError?.message ?? (error instanceof Error ? error.message : "Unknown"),
        ...(appError && { app_error_code: appError.code }),
        ...context,
      },
      {
        category,
        level: isExpectedAuthState || isExpectedAppFailure || isAborted ? "warning" : "error",
      }
    );

    if (isExpectedAuthState || isExpectedAppFailure || isAborted) {
      markErrorObserved(error);
      throw error;
    }

    captureError(error, {
      feature,
      operation,
      requestId,
      tags: appError && { app_error_code: appError.code },
      extra: { kind, durationMs, outcome: failureOutcome, ...context },
    });
    markErrorObserved(error);

    throw error;
  }
}

/**
 * Track a mutation operation with automatic breadcrumbs and error handling.
 *
 * @example
 * ```typescript
 * const result = await trackMutation({
 *   feature: "dataset",
 *   operation: "update",
 *   execute: () => updateDimension({ id, name, description }),
 *   context: { entityId: id, entityName: name },
 * });
 * ```
 */
export async function trackMutation<T>(options: {
  feature: string;
  operation: string;
  execute: () => Promise<T>;
  context?: Record<string, unknown>;
}): Promise<T> {
  return trackObservedOperation({
    kind: "mutation",
    feature: options.feature,
    operation: options.operation,
    execute: () => options.execute(),
    context: options.context,
  });
}

/**
 * Network status types for observability tracking.
 */
export type NetworkStatusType = "loading" | "connected" | "offline" | "reconnecting";

/**
 * Failed reconnection attempts that count as an "extended" disconnection worth
 * a Sentry warning. Convex resets the counter only on a successful connection.
 */
const EXTENDED_RECONNECT_THRESHOLD = 5;

// Guards one Sentry event per disconnection episode (lost connection → next
// successful reconnect). Without it, a tab flapping between offline and
// reconnecting emits one event per flap with an ever-growing retry count
// (LAT-840: thousands of events from a handful of parked tabs).
let extendedReconnectionReported = false;

/** Test-only: reset the per-episode reporting guard. */
export function resetNetworkStatusTrackingForTest() {
  extendedReconnectionReported = false;
}

/**
 * Track a network status change for debugging and monitoring.
 *
 * - Logs breadcrumbs for status transitions
 * - Captures one Sentry warning per disconnection episode once the retry
 *   count crosses EXTENDED_RECONNECT_THRESHOLD
 *
 * Call on every status or retry-count update; the retry threshold is usually
 * crossed while the status is already "reconnecting", not on the transition.
 *
 * @example
 * ```typescript
 * useEffect(() => {
 *   trackNetworkStatusChange(status, previousStatus, connectionRetries);
 * }, [status, connectionRetries]);
 * ```
 */
export function trackNetworkStatusChange(
  status: NetworkStatusType,
  previousStatus: NetworkStatusType | null,
  connectionRetries: number
) {
  if (status === "connected") {
    extendedReconnectionReported = false;
  }

  // Breadcrumb only on real transitions (not initial mount, not retry ticks)
  if (previousStatus !== null && status !== previousStatus) {
    let level: Sentry.SeverityLevel = "info";
    if (status === "offline") {
      level = "warning";
    } else if (status === "reconnecting" && connectionRetries > 3) {
      level = "warning";
    }

    breadcrumb(
      `network.status.${status}`,
      {
        previousStatus,
        connectionRetries,
        transition: `${previousStatus} → ${status}`,
      },
      { category: "network", level }
    );
  }

  // The message deliberately excludes the retry count: Sentry groups default
  // events by message, so embedding the count fragments this signal into one
  // issue per attempt count.
  if (
    status === "reconnecting" &&
    connectionRetries >= EXTENDED_RECONNECT_THRESHOLD &&
    !extendedReconnectionReported
  ) {
    extendedReconnectionReported = true;
    Sentry.captureMessage("Extended network reconnection", {
      level: "warning",
      tags: {
        feature: "network",
        operation: "reconnect",
      },
      extra: {
        connectionRetries,
        previousStatus,
      },
    });
  }
}
